import { useEffect, useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Tour from 'reactour';

import { SubsetSuggestedSubset } from './components/subsets-suggested-subsets';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { GetSubsetsResponseDto } from '../../../../../../../backend/src/matter/subset/dto/get-subsets.response.dto';
import { CreateSubsetButton } from '../components/create-subset-button';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteSubset } from '@/api/mutations/useDeleteSubset';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubsets, hasRecentProcessingSubset } from '@/api/queries/useGetSubsets';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { SubsetsListOnboarding } from '@/new/components/onboarding/subsets-list-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { ProcessingChip, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { SUBSETS_LIST_TOUR_STEPS } from '@/new/constants/tour-steps';
import { useTour } from '@/new/hooks/useTour';
import { formatDatetimeForTable } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

const fallbackTableData: GetSubsetsResponseDto[] = [];
const columnHelper = createColumnHelper<GetSubsetsResponseDto>();

export function Subsets() {
  const { t } = useTranslation();
  const { matterId } = useParams();
  if (!matterId) return null;

  const { isOpen: isTourOpen, openTour, closeTour } = useTour('subsets-list');
  const { data: matter } = useGetMatterV2(matterId);
  const matterBreadcrumbHead = useMatterBreadcrumbHead(matterId, matter?.name, false);
  const { data: subsets } = useGetSubsets(matterId);
  const { getProcessingState } = useMatterProcessingStore();
  const hasProcessingSubset = hasRecentProcessingSubset(subsets);

  // Track page view
  useEffect(() => {
    trackCustomEvent(TrackingEventsV2.SubsetView, { matterId });
  }, [matterId]);

  return (
    <>
      <Breadcrumb
        items={[...matterBreadcrumbHead, { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: true }]}
      />

      <PageContainer>
        <PageHeader className="mb-5">
          <PageTitle
            text="Subsets"
            subtext={`Use Subsets to ${t('analyse')} information from a smaller, focused collection of documents.`}
          />

          {getProcessingState(matterId)?.hasCompletedRuns && (
            <CreateSubsetButton matterId={matterId} hasProcessingSubset={hasProcessingSubset} />
          )}
        </PageHeader>

        <SubsetSuggestedSubset />
      </PageContainer>

      <SubsetsContent matterId={matterId} />

      <SubsetsListOnboarding onTakeTour={openTour} />

      {/* @ts-ignore: Tour unsupported types */}
      <Tour startAt={0} steps={SUBSETS_LIST_TOUR_STEPS} isOpen={isTourOpen} onRequestClose={closeTour} />
    </>
  );
}

function SubsetsContent({ matterId }: { matterId: string }) {
  const { user } = useUserContext();
  const userId = user?.id;
  const [globalFilter, setGlobalFilter] = useState('');
  const [subsetToDelete, setSubsetToDelete] = useState<GetSubsetsResponseDto | null>(null);
  const { mutate: deleteSubset, isLoading: isDeleting } = useDeleteSubset();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => <span>Subset Name</span>,
        cell: (info) => (
          <div className="flex items-center gap-3">
            <Link to={info.row.original.id}>{info.getValue()}</Link>

            {info.row.original.status === 'processing' && (
              <ProcessingChip processingState={ProcessingState.Processing} />
            )}

            {info.row.original.status === 'failed' && <ProcessingChip processingState={ProcessingState.NotProcessed} />}
          </div>
        ),
        meta: {
          sortIcon: 'string',
        },
      }),

      columnHelper.accessor('createdBy.email', {
        header: () => <span>Created By</span>,
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Date Created</span>,
        cell: (info) => formatDatetimeForTable(info.getValue()),
      }),
      columnHelper.accessor('docCount', {
        header: () => <span>No. of Docs</span>,
        cell: (info) => {
          const count = info.getValue();
          return count !== undefined ? `${count}` : '-';
        },
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.accessor('factsCount', {
        header: () => <span>No. of Facts</span>,
        cell: (info) => {
          const count = info.getValue();
          return count !== undefined ? `${count}` : '0';
        },
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          if (info.row.original.isAllDocuments) return null;
          return (
            <PermissionGate permission={Permission.SubsetDelete} orAllowed={info.row.original.createdBy?.id === userId}>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setSubsetToDelete(info.row.original);
                }}
              >
                <Icon className="lucide--trash-2 h-4 w-4 text-gray-500 hover:text-red-600" />
              </Button>
            </PermissionGate>
          );
        },
      }),
    ],
    [],
  );

  const navigate = useNavigate();
  const { data: subsets, isLoading } = useGetSubsets(matterId);

  // Filter out isAllDocuments case from the table - memoized to prevent unnecessary table recalculations
  const regularCases = useMemo(() => subsets?.filter((c) => !c.isAllDocuments) ?? fallbackTableData, [subsets]);

  const table = useReactTable({
    data: regularCases,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        plural="subsets"
        rowOnClick={(row) => navigate(row.original.id)}
        searchDataTour="subsets-search"
        tableDataTour="subsets-table"
        loading={isLoading}
      />

      <ConfirmationModal
        isOpen={!!subsetToDelete}
        onOpenChange={(open) => !open && setSubsetToDelete(null)}
        title="Delete Subset"
        onConfirm={() => {
          if (subsetToDelete && matterId) {
            deleteSubset(
              { matterId, subsetId: subsetToDelete.id },
              {
                onSuccess: () => setSubsetToDelete(null),
              },
            );
          }
        }}
        isLoading={isDeleting}
      >
        <p>
          Are you sure you want to delete <strong>{subsetToDelete?.name}</strong>? <br /> <br />
          This will remove any chronologies or Kim answers associated with this subset, but not remove documents or
          facts. This action cannot be undone.
        </p>
      </ConfirmationModal>
    </>
  );
}
